import { useEffect, useState } from "react";
import { ImageGalleryElement } from '../../components/reusable-elements/image-gallery-element';
import { Link, useParams } from "react-router-dom";
import './gallery.css';

export const PropertyBrochureTab = ({ propertyDocument, photos, setShowEditImageModal, setStarRating, onPropertyChange, handleSaveProperty }) => {
    const { id } = useParams();
    const rooms = propertyDocument?.property?.rooms ?? [];

    const photoList = Array.isArray(photos) ? photos : [];
    const primaryImage = photoList.find(p => p?.isPrimaryImage && (p.thumbPathIncSASToken || p.pathIncSASToken))
        || photoList.find(p => p && (p.thumbPathIncSASToken || p.pathIncSASToken));

    const propertyDescription = (typeof propertyDocument?.property?.description === 'string'
        ? propertyDocument.property.description.trim()
        : '') || '';

    const [descriptionDraft, setDescriptionDraft] = useState(propertyDescription);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        setDescriptionDraft(propertyDescription);
    }, [propertyDescription]);

    const handleSaveDescription = async () => {
        if (!propertyDocument) return;
        const updatedDoc = {
            ...propertyDocument,
            property: {
                ...(propertyDocument.property ?? {}),
                description: (descriptionDraft || '').trim()
            }
        };

        if (typeof onPropertyChange === 'function') {
            onPropertyChange(updatedDoc);
            return;
        }

        if (typeof handleSaveProperty === 'function') {
            setSaving(true);
            try {
                // Some callers expect just the property object, others the full document.
                try {
                    await handleSaveProperty(updatedDoc.property);
                } catch (err) {
                    console.warn('handleSaveProperty(updatedDoc.property) threw, retrying with full document.', err);
                    await handleSaveProperty(updatedDoc);
                }
            } catch (err2) {
                console.error('Failed to save property description', err2, { updatedDoc });
            } finally {
                setSaving(false);
            }
        }
    };

    // sort rooms for display: bedrooms with numeric roomNumber first in numeric order,
    // then other rooms (sorted by type then id for determinism)
    const sortedRooms = [...rooms].sort((a, b) => {
        const aType = a?.roomType || '';
        const bType = b?.roomType || '';
        const aIsBedroom = aType === 'Bedroom';
        const bIsBedroom = bType === 'Bedroom';
        const aNum = typeof a?.roomNumber === 'number' ? a.roomNumber : parseInt(a?.roomNumber, 10);
        const bNum = typeof b?.roomNumber === 'number' ? b.roomNumber : parseInt(b?.roomNumber, 10);
        const aHasNum = aIsBedroom && !Number.isNaN(aNum);
        const bHasNum = bIsBedroom && !Number.isNaN(bNum);

        if (aHasNum && bHasNum) return aNum - bNum;
        if (aHasNum && !bHasNum) return -1;
        if (!aHasNum && bHasNum) return 1;

        if (aIsBedroom && !bIsBedroom) return -1;
        if (!aIsBedroom && bIsBedroom) return 1;

        if (aType !== bType) return aType.localeCompare(bType);
        const aId = a?.id || a?._id || a?.roomId || '';
        const bId = b?.id || b?._id || b?.roomId || '';
        return String(aId).localeCompare(String(bId));
    });

    const buildRoomLabel = (room) => {
        const type = room?.roomType || 'Room';
        if (type === 'Bedroom') {
            return room?.roomNumber ? `Bedroom ${room.roomNumber}` : 'Bedroom';
        }
        return type;
    };

    return (
        <div className="tab-pane fade" id="nav-pills-tab-brochure">
            <div className="d-print-none mb-3 text-end">
                <Link
                    to={`/property/detail/${id}/brochure-print`}
                    className="btn btn-sm btn-outline-primary"
                >
                    Open print-friendly brochure
                </Link>
            </div>

            <div className="gallery-container">
                {(primaryImage || true) && (
                    <div className="mb-4 brochure-header-block">
                        <div className="brochure-main-image-wrapper">
                            <h4 className="mb-2">Property description</h4>
                            {primaryImage && (
                                <img
                                    src={primaryImage.thumbPathIncSASToken || primaryImage.pathIncSASToken}
                                    alt={primaryImage.title || 'Main property photo'}
                                    className="brochure-main-image"
                                />
                            )}
                        </div>

                        <div className="brochure-header-text">
                            <textarea
                                className="form-control"
                                rows={5}
                                style={{ minHeight: '7.5rem', height: 'auto' }}
                                value={descriptionDraft}
                                onChange={e => setDescriptionDraft(e.target.value)}
                                placeholder="Enter a short marketing description for this property"
                            />
                        </div>
                    </div>
                )}

                <div className="mb-3 text-end">
                    <button
                        type="button"
                        className="btn btn-sm btn-primary"
                        onClick={handleSaveDescription}
                        disabled={saving}
                    >
                        {saving ? 'Saving...' : 'Save description'}
                    </button>
                </div>

                <div className="mb-4">
                    <h4 className="mb-3">Gallery</h4>
                    <ImageGalleryElement
                        key="photo-gallery-images"
                        photos={photos ?? []}
                        setShowEditImageModal={setShowEditImageModal}
                        setStarRating={setStarRating}
                        enableSlideshow={false}
                    />
                </div>

                <div>
                    <h4 className="mb-3">Rooms</h4>
                    {sortedRooms.map((room, index) => {
                        const gallery = Array.isArray(room?.mediaGallery) ? room.mediaGallery : [];
                        const label = buildRoomLabel(room);
                        const roomKey = room?.id || room?._id || room?.roomId || `room-${index}`;
                        const blockClass = index > 0 ? "pt-3 mt-3 border-top" : "pt-1";

                        const description = typeof room.description === 'string' ? room.description : '';

                        const hasMedia = Array.isArray(gallery) && gallery.length > 0;
                        const hasDescription = !!(description && description.trim());
                        if (!hasMedia && !hasDescription) return null;

                        return (
                            <div key={roomKey} className={blockClass}>
                                <h5 className="mb-2">{label}</h5>
                                {hasDescription && (
                                    <p className="mb-3 brochure-room-description">{description}</p>
                                )}
                                {!hasMedia && (
                                    <p className="brochure-room-placeholder d-print-none mb-3">
                                        Add photos or videos for this room from the Rooms tab to showcase it in the brochure.
                                    </p>
                                )}
                                {hasMedia && (
                                    <ImageGalleryElement
                                        key={`room-media-${roomKey}`}
                                        photos={gallery}
                                        setShowEditImageModal={setShowEditImageModal}
                                        setStarRating={setStarRating}
                                        getImageContextLabel={() => label}
                                        enableSlideshow={false}
                                    />
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};
